// Lazy-loaded PDF engine. `getEngine()` triggers a dynamic import of
// `pdfjs-dist` + `@recto/pdf-engine`; Vite splits this into a
// separate chunk so the Landing route never pays the cost. The
// first `getEngine()` call happens when the user clicks Open on
// Landing (or drops a file) — same event that fires the picker, so
// the two async loads (engine chunk + user's file bytes) parallelise.
// The chunk-size budget lives in the P-01.14 shipping gates,
// verified by the Playwright + bundle-budget checks landing with #39.

import type { IPdfEngine } from '@recto/pdf-engine';

// Cached across the app's lifetime — one engine, many documents. The
// engine's internal `docs` Map keys by generated id, so multiple
// concurrent loads coexist safely.
let enginePromise: Promise<IPdfEngine> | null = null;

/**
 * Returns the app-wide `PdfJsPdfEngine` instance, loading pdfjs +
 * configuring the worker on first call. Subsequent calls return the
 * cached instance immediately.
 *
 * The dynamic imports below (`pdfjs-dist/legacy/build/pdf.mjs`,
 * `pdfjs-dist/build/pdf.worker.mjs?worker&url`, `@recto/pdf-engine`)
 * become their own Vite chunk. This chunk lives OUTSIDE the Landing
 * route's initial bundle — inspect `apps/web/dist/assets/*.js` after
 * `pnpm --filter @recto/web build` to confirm the split.
 */
export async function getEngine(): Promise<IPdfEngine> {
  if (enginePromise === null) {
    enginePromise = loadEngine();
  }
  return enginePromise;
}

async function loadEngine(): Promise<IPdfEngine> {
  const [{ GlobalWorkerOptions }, workerUrlMod, { PdfJsPdfEngine }] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    // Vite `?worker&url` resolves to the built worker asset URL.
    import('pdfjs-dist/build/pdf.worker.mjs?worker&url'),
    import('@recto/pdf-engine'),
  ]);
  GlobalWorkerOptions.workerSrc = workerUrlMod.default;
  return new PdfJsPdfEngine();
}
